import React, { useState } from 'react';
import { ModulePermission, PermissionAction, PermissionScope, ModuleCategory } from '@troia-v3/types';
import { useModules, ModuleDefinition } from '../hooks/useModules';
import { Icon } from '@/shared/icons'
import { Checkbox, RadioGroup } from '@/shared/components/ui';
// Module category labels
const MODULE_CATEGORIES: Record<ModuleCategory, string> = {
  core: 'Módulos Principais',
  business: 'Módulos de Negócio',
  analytics: 'Análise e Relatórios',
  integration: 'Integrações',
  automation: 'Automação',
  marketing: 'Marketing',
  communication: 'Comunicação',
  dashboards: 'Dashboards',
};

// Action labels
const ACTION_LABELS: Record<PermissionAction, string> = {
  create: 'Criar',
  read: 'Visualizar',
  update: 'Editar',
  delete: 'Excluir',
  export: 'Exportar',
  import: 'Importar',
  assign: 'Atribuir',
  attend: 'Atender',
};

// Scope labels
const SCOPE_LABELS: Record<PermissionScope, string> = {
  own: 'Próprios',
  team: 'Da Equipe',
  all: 'Todos',
};

interface PermissionSelectorProps {
  permissions: Record<string, ModulePermission>;
  onChange: (permissions: Record<string, ModulePermission>) => void;
  disabled?: boolean;
}

export const PermissionSelector: React.FC<PermissionSelectorProps> = ({
  permissions,
  onChange,
  disabled = false
}) => {
  // ✅ Fetch modules from backend
  const { data: modulesData, isLoading, isError } = useModules();

  const [expandedCategories, setExpandedCategories] = useState<string[]>(['core', 'business']);
  const [expandedModules, setExpandedModules] = useState<string[]>([]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleModuleExpansion = (moduleName: string) => {
    setExpandedModules(prev =>
      prev.includes(moduleName)
        ? prev.filter(m => m !== moduleName)
        : [...prev, moduleName]
    );
  };

  const isModuleEnabled = (moduleName: string): boolean => {
    return !!permissions[moduleName];
  };

  const toggleModule = (moduleName: string, moduleDefinition: ModuleDefinition) => {
    const newPermissions = { ...permissions };

    if (isModuleEnabled(moduleName)) {
      // Disable module
      delete newPermissions[moduleName];
    } else {
      // Enable module with default permissions
      newPermissions[moduleName] = {
        actions: moduleDefinition.availableActions,
        scope: moduleDefinition.defaultScope
      };
    }

    onChange(newPermissions);
  };

  const toggleAction = (moduleName: string, action: PermissionAction) => {
    const newPermissions = { ...permissions };
    const modulePermission = newPermissions[moduleName];

    if (!modulePermission) return;

    const actions = modulePermission.actions;
    const hasAction = actions.includes(action);

    if (hasAction) {
      // Remove action
      modulePermission.actions = actions.filter(a => a !== action);

      // If no actions left, remove module
      if (modulePermission.actions.length === 0) {
        delete newPermissions[moduleName];
      }
    } else {
      // Add action
      modulePermission.actions = [...actions, action];
    }

    onChange(newPermissions);
  };

  const updateScope = (moduleName: string, scope: PermissionScope) => {
    const newPermissions = { ...permissions };
    const modulePermission = newPermissions[moduleName];

    if (!modulePermission) return;

    modulePermission.scope = scope;
    onChange(newPermissions);
  };

  const hasAction = (moduleName: string, action: PermissionAction): boolean => {
    return permissions[moduleName]?.actions.includes(action) || false;
  };

  const getModuleScope = (moduleName: string): PermissionScope => {
    return permissions[moduleName]?.scope || 'own';
  };

  // Loading state
  if (isLoading) {
    return (
      <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
        Carregando módulos disponíveis...
      </p>
    );
  }

  // Error state
  if (isError || !modulesData) {
    return (
      <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
        Erro ao carregar módulos. Tente recarregar a página.
      </p>
    );
  }

  // ✅ Use modules from backend API
  const ALL_MODULES = modulesData?.all || [];

  // Group modules by category
  const modulesByCategory = ALL_MODULES.reduce((acc: Record<string, ModuleDefinition[]>, module: ModuleDefinition) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {} as Record<string, ModuleDefinition[]>);

  return (
    <div className="space-y-4">
      {/* Categories */}
      {Object.entries(modulesByCategory).map(([category, modules]: [string, ModuleDefinition[]]) => {
        const enabledCount = modules.filter((m) => isModuleEnabled(m.name)).length;
        return (
        <div
          key={category}
          className="border rounded-lg overflow-hidden"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          {/* Category Header */}
          <button
            type="button"
            className="w-full flex items-center gap-3 p-3 transition-colors"
            style={{
              backgroundColor: 'var(--theme-dropdown-hover)',
              color: 'var(--theme-modal-text)',
            }}
            onClick={() => toggleCategory(category)}
            disabled={disabled}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-dropdown-hover)';
            }}
          >
            <Icon
              name="chevron-right"
              className="w-4 h-4 transition-transform"
              style={{ transform: expandedCategories.includes(category) ? 'rotate(90deg)' : 'none' }}
            />
            <span className="font-medium flex-1 text-left">
              {MODULE_CATEGORIES[category as keyof typeof MODULE_CATEGORIES]}
            </span>
            <span className="text-xs" style={{ color: 'var(--theme-dropdown-text-muted)' }}>
              {enabledCount > 0 ? `${enabledCount} de ` : ''}{modules.length} módulos
            </span>
          </button>

          {/* Modules List */}
          {expandedCategories.includes(category) && (
            <div>
              {modules.map((module, index) => {
                const isEnabled = isModuleEnabled(module.name);
                const isExpanded = expandedModules.includes(module.name);

                return (
                  <div
                    key={module.name}
                    className={index > 0 ? "border-t" : ""}
                    style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
                  >
                    {/* Module Row */}
                    <div className="p-3 space-y-3">
                      {/* Module Header */}
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isEnabled}
                          onChange={() => toggleModule(module.name, module)}
                          disabled={disabled}
                          data-testid={`level-perm-module-${module.name}`}
                        />
                        <div className="flex-1">
                          <button
                            type="button"
                            className="text-left w-full"
                            onClick={() => {
                              if (isEnabled) toggleModuleExpansion(module.name);
                            }}
                            disabled={disabled || !isEnabled}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4
                                  className="font-medium text-sm"
                                  style={{ color: 'var(--theme-modal-text)' }}
                                >
                                  {module.displayName}
                                </h4>
                                <p
                                  className="text-xs mt-0.5"
                                  style={{ color: 'var(--theme-dropdown-text-muted)' }}
                                >
                                  {module.description}
                                </p>
                              </div>
                              {isEnabled && (
                                <div className="ml-2">
                                  {isExpanded ? (
                                    <Icon name="chevron-down" className="w-4 h-4" style={{ color: 'var(--theme-modal-text)' }} />
                                  ) : (
                                    <Icon name="chevron-right" className="w-4 h-4" style={{ color: 'var(--theme-modal-text)' }} />
                                  )}
                                </div>
                              )}
                            </div>
                          </button>

                          {/* Module Details (when enabled and expanded) */}
                          {isEnabled && isExpanded && (
                            <div className="mt-3 space-y-3">
                              {/* Actions */}
                              <div>
                                <label
                                  className="block text-xs font-medium mb-2"
                                  style={{ color: 'var(--theme-modal-text)' }}
                                >
                                  Ações Permitidas:
                                </label>
                                <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
                                  {module.availableActions.map((action: PermissionAction) => (
                                    <Checkbox
                                      key={action}
                                      checked={hasAction(module.name, action)}
                                      onChange={() => toggleAction(module.name, action)}
                                      disabled={disabled}
                                      label={ACTION_LABELS[action]}
                                    />
                                  ))}
                                </div>
                              </div>

                              {/* Scope */}
                              {module.requiresScope && (
                                <div>
                                  <label
                                    className="block text-xs font-medium mb-2"
                                    style={{ color: 'var(--theme-modal-text)' }}
                                  >
                                    Escopo de Acesso:
                                  </label>
                                  <RadioGroup
                                    orientation="horizontal"
                                    name={`scope-${module.name}`}
                                    value={getModuleScope(module.name)}
                                    onChange={(v) => updateScope(module.name, v as PermissionScope)}
                                    options={(['own', 'team', 'all'] as PermissionScope[]).map((scope) => ({
                                      value: scope,
                                      label: SCOPE_LABELS[scope],
                                    }))}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};
